import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { Send, MapPin, Building2, Check, Briefcase, ChevronDown, X, Info } from 'lucide-react';

const IRISH_COUNTIES = ["Carlow","Cavan","Clare","Cork","Donegal","Dublin","Galway","Kerry","Kildare","Kilkenny","Laois","Leitrim","Limerick","Longford","Louth","Mayo","Meath","Monaghan","Offaly","Roscommon","Sligo","Tipperary","Waterford","Westmeath","Wexford","Wicklow"];

const ApplyPage = () => {
  const { user } = useAuth();
  const [myApps, setMyApps] = useState([]);
  const [selectedCounties, setSelectedCounties] = useState([]);
  const [employers, setEmployers] = useState({});
  const [selectedEmployers, setSelectedEmployers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [directEmployers, setDirectEmployers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countyDropdown, setCountyDropdown] = useState(false);

  const [appType, setAppType] = useState('proactive');

  useEffect(() => {
    axios.get(`${API_BASE}/applications/apprentice/${user.id}`)
      .then(res => setMyApps(res.data.applications || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.id]);

  const fetchEmployers = useCallback(async (counties) => {
    if (counties.length === 0) { setEmployers({}); setDirectEmployers({}); return; }
    try {
      const [proactiveRes, directRes] = await Promise.all([
        axios.get(`${API_BASE}/employers/by-counties?counties=${counties.join(',')}`),
        axios.get(`${API_BASE}/employers/with-roles?counties=${counties.join(',')}`),
      ]);
      setEmployers(proactiveRes.data.counties || {});
      setDirectEmployers(directRes.data.counties || {});
    } catch (err) {
      console.error('Failed to fetch employers:', err);
    }
  }, []);

  const toggleCounty = (county) => {
    const updated = selectedCounties.includes(county)
      ? selectedCounties.filter(c => c !== county)
      : [...selectedCounties, county];
    setSelectedCounties(updated);
    setSelectedEmployers([]);
    setSelectedRoles([]);
    fetchEmployers(updated);
  };

  const toggleEmployer = (empId) => {
    setSelectedEmployers(prev =>
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    );
  };

  const toggleRole = (roleId) => {
    setSelectedRoles(prev =>
      prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId]
    );
  };

  const selectAll = () => {
    const allIds = filteredAll.map(e => e.id);
    setSelectedEmployers(allIds);
  };

  const handleSubmit = async () => {
    if (appType === 'direct' && selectedRoles.length === 0) { toast.error('Select at least one role'); return; }
    if (appType === 'proactive' && selectedEmployers.length === 0) { toast.error('Select at least one employer'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/applications/submit`, {
        user_id: user.id,
        type: appType,
        employer_ids: appType === 'proactive' ? selectedEmployers : [],
        role_ids: appType === 'direct' ? selectedRoles : [],
      });
      const count = appType === 'direct' ? selectedRoles.length : selectedEmployers.length;
      toast.success(`Application sent to ${count} ${appType === 'direct' ? 'role' : 'employer'}${count > 1 ? 's' : ''}!`);
      setSubmitted(true);
      const res = await axios.get(`${API_BASE}/applications/apprentice/${user.id}`);
      setMyApps(res.data.applications || []);
    } catch (err) {
      toast.error('Failed to submit applications');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-12 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" /></div>;
  }

  const allEmployers = Object.values(employers).flat();
  const filteredEmployers = appType === 'direct'
    ? Object.fromEntries(Object.entries(employers).map(([county, emps]) => [county, emps.filter(e => e.openings > 0)]).filter(([_, emps]) => emps.length > 0))
    : employers;
  const filteredAll = Object.values(filteredEmployers).flat();
  const alreadyApplied = new Set(myApps.map(a => a.employer_id));

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1d1d1f] mb-3" data-testid="apply-title">
            Apply to Employers
          </h1>
          <p className="text-lg text-[#86868b]">
            Your complete candidate package is ready. Choose employers to send it to.
          </p>
        </div>

        {/* Previous Applications */}
        {myApps.length > 0 && (
          <div className="bg-green-50 rounded-2xl border border-green-200 p-5 mb-6">
            <h3 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
              <Check className="w-4 h-4" /> Applications Sent ({myApps.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {myApps.map(a => (
                <span key={a.application_id} className="text-xs bg-white border border-green-300 text-green-700 px-3 py-1 rounded-full">
                  {a.employer_id.split('-').slice(1).join(' ').replace(/(^|\s)\S/g, l => l.toUpperCase())}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Two Routes */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <button
            onClick={() => { setAppType('direct'); setSelectedEmployers([]); }}
            className={`text-left rounded-2xl border-2 p-6 transition-all ${appType === 'direct' ? 'border-[#34C759] bg-green-50' : 'border-[#d2d2d7] bg-white hover:border-[#86868b]'}`}
            data-testid="route-direct"
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${appType === 'direct' ? 'bg-green-100' : 'bg-[#f5f5f7]'}`}>
              <Briefcase className={`w-5 h-5 ${appType === 'direct' ? 'text-[#34C759]' : 'text-[#86868b]'}`} />
            </div>
            <h3 className="text-lg font-semibold text-[#1d1d1f] mb-1">Direct Application</h3>
            <p className="text-sm text-[#86868b]">Apply for existing advertised apprenticeship positions with current openings.</p>
          </button>
          <button
            onClick={() => { setAppType('proactive'); setSelectedEmployers([]); }}
            className={`text-left rounded-2xl border-2 p-6 transition-all ${appType === 'proactive' ? 'border-[#0071e3] bg-blue-50' : 'border-[#d2d2d7] bg-white hover:border-[#86868b]'}`}
            data-testid="route-proactive"
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${appType === 'proactive' ? 'bg-blue-100' : 'bg-[#f5f5f7]'}`}>
              <Send className={`w-5 h-5 ${appType === 'proactive' ? 'text-[#0071e3]' : 'text-[#86868b]'}`} />
            </div>
            <h3 className="text-lg font-semibold text-[#1d1d1f] mb-1">Proactive Application</h3>
            <p className="text-sm text-[#86868b]">Forward your complete package to employers you'd like to work for, even if they have no open positions.</p>
          </button>
        </div>

        {/* Step 1: Select Counties */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-6">
          <h2 className="text-xl font-semibold text-[#1d1d1f] mb-1 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#0071e3]" />
            Step 1: Select Counties
          </h2>
          <p className="text-sm text-[#86868b] mb-4">Choose one or more counties where you'd like to work. No limit.</p>

          <div className="relative">
            <button
              onClick={() => setCountyDropdown(!countyDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 border border-[#d2d2d7] rounded-xl bg-white text-left hover:border-[#86868b] transition-colors"
              data-testid="county-multi-selector"
            >
              <span className={selectedCounties.length > 0 ? 'text-[#1d1d1f] font-medium' : 'text-[#86868b]'}>
                {selectedCounties.length > 0 ? `${selectedCounties.length} ${selectedCounties.length === 1 ? 'county' : 'counties'} selected` : 'Select counties...'}
              </span>
              <ChevronDown className={`w-4 h-4 text-[#86868b] transition-transform ${countyDropdown ? 'rotate-180' : ''}`} />
            </button>
            {countyDropdown && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-[#d2d2d7] rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {IRISH_COUNTIES.map(county => (
                  <button
                    key={county}
                    onClick={() => toggleCounty(county)}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#f5f5f7] transition-colors flex items-center justify-between ${
                      selectedCounties.includes(county) ? 'bg-blue-50 text-[#0071e3] font-medium' : 'text-[#1d1d1f]'
                    }`}
                    data-testid={`county-apply-${county}`}
                  >
                    <span>{county}</span>
                    {selectedCounties.includes(county) && <Check className="w-4 h-4 text-[#0071e3]" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedCounties.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selectedCounties.map(c => (
                <span key={c} className="text-xs bg-blue-50 text-[#0071e3] px-3 py-1 rounded-full flex items-center gap-1.5 font-medium">
                  {c}
                  <button onClick={() => toggleCounty(c)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Step 2: Select Employers / Roles */}
        {selectedCounties.length > 0 && appType === 'proactive' && (
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-[#1d1d1f] flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#34C759]" />
                  Step 2: Select Employers
                </h2>
                <p className="text-sm text-[#86868b]">{filteredAll.length} employers found. Select who to send your package to.</p>
              </div>
              {filteredAll.length > 0 && (
                <Button onClick={selectAll} variant="ghost" className="text-sm text-[#0071e3]" data-testid="select-all-employers">
                  Select All
                </Button>
              )}
            </div>
            {filteredAll.length === 0 ? (
              <p className="text-sm text-[#86868b] text-center py-6">No registered employers found in the selected counties.</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(filteredEmployers).map(([county, emps]) => (
                  <div key={county}>
                    <p className="text-xs font-semibold text-[#86868b] uppercase tracking-wider mb-2 mt-4 first:mt-0">{county}</p>
                    {emps.map(emp => {
                      const isSelected = selectedEmployers.includes(emp.id);
                      const applied = alreadyApplied.has(emp.id);
                      return (
                        <button key={emp.id} onClick={() => !applied && toggleEmployer(emp.id)} disabled={applied}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all mb-1.5 ${applied ? 'bg-green-50 border border-green-200 opacity-70' : isSelected ? 'bg-blue-50 border-2 border-[#0071e3]' : 'bg-[#f5f5f7] border-2 border-transparent hover:border-[#d2d2d7]'}`}
                          data-testid={`employer-${emp.id}`}
                        >
                          {applied ? <Check className="w-5 h-5 text-green-600 flex-shrink-0" /> : isSelected ? <div className="w-5 h-5 rounded bg-[#0071e3] flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-white" /></div> : <div className="w-5 h-5 rounded border-2 border-[#d2d2d7] flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#1d1d1f]">{emp.name}</p>
                            <p className="text-xs text-[#86868b]">{emp.sector}</p>
                          </div>
                          {emp.openings > 0 && <span className="text-xs text-[#34C759] bg-green-50 px-2 py-0.5 rounded-full flex-shrink-0">{emp.openings} openings</span>}
                          {applied && <span className="text-xs text-green-600 font-medium flex-shrink-0">Applied</span>}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2 (Direct): Employers with Roles */}
        {selectedCounties.length > 0 && appType === 'direct' && (
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-6">
            <h2 className="text-xl font-semibold text-[#1d1d1f] mb-1 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#34C759]" />
              Step 2: Select Roles to Apply For
            </h2>
            <p className="text-sm text-[#86868b] mb-4">Tick the specific apprenticeship roles you're interested in.</p>

            {Object.keys(directEmployers).length === 0 ? (
              <p className="text-sm text-[#86868b] text-center py-6">No current openings found in the selected counties.</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(directEmployers).map(([county, emps]) => (
                  <div key={county}>
                    <p className="text-xs font-semibold text-[#86868b] uppercase tracking-wider mb-3 mt-5 first:mt-0">{county}</p>
                    {emps.map(emp => (
                      <div key={emp.employer} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Building2 className="w-4 h-4 text-[#1d1d1f]" />
                          <h4 className="text-sm font-semibold text-[#1d1d1f]">{emp.employer}</h4>
                          <span className="text-xs text-[#86868b]">&middot; {emp.location}</span>
                        </div>
                        <div className="ml-6 space-y-1.5">
                          {emp.roles.map(role => {
                            const isSelected = selectedRoles.includes(role.id);
                            return (
                              <div key={role.id} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isSelected ? 'bg-green-50 border-2 border-[#34C759]' : 'bg-[#f5f5f7] border-2 border-transparent hover:border-[#d2d2d7]'}`}>
                                <button onClick={() => toggleRole(role.id)} className="flex-shrink-0" data-testid={`role-${role.id}`}>
                                  {isSelected ? (
                                    <div className="w-5 h-5 rounded bg-[#34C759] flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>
                                  ) : (
                                    <div className="w-5 h-5 rounded border-2 border-[#d2d2d7]" />
                                  )}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#1d1d1f]">{role.title}</p>
                                  <p className="text-xs text-[#86868b]">{role.category}</p>
                                </div>
                                {role.closing_date && (
                                  <span className="text-xs text-[#86868b] flex-shrink-0">Closes: {new Date(role.closing_date).toLocaleDateString('en-IE')}</span>
                                )}
                                <div className="relative group flex-shrink-0">
                                  <Info className="w-4 h-4 text-[#86868b] cursor-help" />
                                  <div className="absolute right-0 top-6 z-30 w-72 bg-[#1d1d1f] text-white text-xs rounded-xl p-3 shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                                    {role.description}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Submit */}
        {(appType === 'proactive' ? selectedEmployers.length > 0 : selectedRoles.length > 0) && (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full h-12 text-base"
            data-testid="submit-applications-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {submitting ? 'Sending...' : appType === 'direct'
              ? `Apply for ${selectedRoles.length} Role${selectedRoles.length > 1 ? 's' : ''}`
              : `Send Package to ${selectedEmployers.length} Employer${selectedEmployers.length > 1 ? 's' : ''}`}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ApplyPage;
